import { useState, useEffect } from 'react';
import { Menu, X, Phone, Mail, Facebook, Instagram, Youtube, Linkedin, Star, Globe } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import ThemeToggle from './ThemeToggle';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [isMainPage, setIsMainPage] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { theme } = useTheme();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };

    const checkCurrentPage = () => {
      const currentPath = location.pathname;
      setIsMainPage(currentPath === '/');
    };

    checkCurrentPage();
    window.addEventListener('scroll', handleScroll);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [location]);

  const scrollToSection = (id: string) => {
    if (location.pathname === '/apnaeduhub') {
      const element = document.getElementById(id);
      if (element) {
        const offset = 80;
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - offset;
        
        // Update URL hash
        window.history.pushState(null, '', `${location.pathname}#${id}`);
        
        // Check if mobile device
        const isMobile = window.innerWidth < 768;
        
        window.scrollTo({
          top: offsetPosition,
          behavior: isMobile ? 'auto' : 'smooth'
        });
      }
    } else if (location.pathname !== '/') {
      navigate(`/#${id}`);
      setTimeout(() => {
        const element = document.getElementById(id);
        if (element) {
          const offset = 80;
          const elementPosition = element.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - offset;
          
          // Check if mobile device
          const isMobile = window.innerWidth < 768;
          
          window.scrollTo({
            top: offsetPosition,
            behavior: isMobile ? 'auto' : 'smooth'
          });
        }
      }, 100);
    } else {
      const element = document.getElementById(id);
      if (element) {
        const offset = 80;
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - offset;
        
        // Update URL hash
        window.history.pushState(null, '', `${location.pathname}#${id}`);
        
        // Check if mobile device
        const isMobile = window.innerWidth < 768;
        
        window.scrollTo({
          top: offsetPosition,
          behavior: isMobile ? 'auto' : 'smooth'
        });
      }
    }
    setIsOpen(false);
  };

  const handleLogoClick = () => {
    if (location.pathname === '/') {
      // Check if mobile device
      const isMobile = window.innerWidth < 768;
      window.scrollTo({ top: 0, behavior: isMobile ? 'auto' : 'smooth' });
    } else {
      navigate('/');
    }
  };

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
      scrolled 
        ? theme === 'light' ? 'bg-white/95 backdrop-blur-xl shadow-lg border-b border-gray-200' : 'glass shadow-premium'
        : (isMainPage 
            ? 'bg-transparent' 
            : theme === 'light' ? 'bg-white/95 backdrop-blur-xl border-b border-gray-200' : 'glass'
          )
    }`}>
      
      {/* Premium Top Bar */}
      <div className={`py-2 text-sm border-b transition-all duration-500 hidden lg:block ${
        scrolled || !isMainPage 
          ? theme === 'light' 
            ? 'bg-gray-50 border-gray-200' 
            : 'glass border-white/10'
          : theme === 'light'
            ? 'bg-white/80 backdrop-blur-xl border-gray-200'
            : 'bg-slate-900/50 backdrop-blur-xl border-white/10'
      }`}>
        <div className="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            
            {/* Left Side - Contact Info */}
            <div className="flex items-center space-x-4">
              <div className="flex items-center gap-2 px-3 py-1 rounded-full shadow-lg bg-gradient-to-r from-blue-600 to-purple-600">
                <Star className="w-3 h-3 text-yellow-300" />
                <span className="text-xs font-semibold text-white">Premium Support</span>
              </div>
              
              <div className="flex items-center space-x-4">
                <a 
                  href="tel:+919548667656" 
                  className={`flex items-center gap-2 px-2 py-1 transition-all duration-300 rounded-lg group ${
                    theme === 'light'
                      ? 'text-gray-600 hover:text-blue-600 hover:bg-blue-50'
                      : 'text-gray-300 hover:text-blue-400 hover:bg-blue-400/10'
                  }`}
                >
                  <Phone className="w-3 h-3 group-hover:animate-pulse" />
                  <span className="text-xs font-medium">+91 95486 67656</span>
                </a>
                
                <a 
                  href="mailto:sales@neurosparktechnologies.in" 
                  className={`flex items-center gap-2 px-2 py-1 transition-all duration-300 rounded-lg group ${
                    theme === 'light'
                      ? 'text-gray-600 hover:text-blue-600 hover:bg-blue-50'
                      : 'text-gray-300 hover:text-blue-400 hover:bg-blue-400/10'
                  }`}
                >
                  <Mail className="w-3 h-3 group-hover:animate-pulse" />
                  <span className="hidden text-xs font-medium xl:inline">sales@neurosparktechnologies.in</span>
                  <span className="text-xs font-medium xl:hidden">Email Us</span>
                </a>
              </div>
            </div>

            {/* Right Side - Social & Language */}
            <div className="flex items-center space-x-3">
              <ThemeToggle className="scale-75" position="relative" />
              
              <div className={`w-px h-3 ${theme === 'light' ? 'bg-gray-300' : 'bg-gray-600'}`}></div>
              
              <div className="flex items-center gap-1">
                <Globe className={`w-3 h-3 ${theme === 'light' ? 'text-gray-500' : 'text-gray-400'}`} />
                <select className={`text-xs bg-transparent border-none outline-none cursor-pointer ${
                  theme === 'light' ? 'text-gray-600' : 'text-gray-300'
                }`}>
                  <option value="en" className={theme === 'light' ? 'bg-white' : 'bg-slate-800'}>English</option>
                  <option value="hi" className={theme === 'light' ? 'bg-white' : 'bg-slate-800'}>हिंदी</option>
                </select>
              </div>
              
              <div className={`w-px h-3 ${theme === 'light' ? 'bg-gray-300' : 'bg-gray-600'}`}></div>
              
              <div className="flex items-center space-x-1">
                <span className={`mr-1 text-xs font-medium ${
                  theme === 'light' ? 'text-gray-500' : 'text-gray-400'
                }`}>Follow Us</span>
                <div className="flex items-center space-x-1">
                  <a href="#" className={`p-1 transition-all duration-300 rounded hover:scale-110 ${
                    theme === 'light'
                      ? 'text-gray-500 hover:text-blue-600 hover:bg-blue-50'
                      : 'text-gray-400 hover:text-blue-400 hover:bg-blue-400/10'
                  }`}>
                    <Facebook className="w-3 h-3" />
                  </a>
                  <a href="#" className={`p-1 transition-all duration-300 rounded hover:scale-110 ${
                    theme === 'light'
                      ? 'text-gray-500 hover:text-pink-600 hover:bg-pink-50'
                      : 'text-gray-400 hover:text-pink-400 hover:bg-pink-400/10'
                  }`}>
                    <Instagram className="w-3 h-3" />
                  </a>
                  <a href="#" className={`p-1 transition-all duration-300 rounded hover:scale-110 ${
                    theme === 'light'
                      ? 'text-gray-500 hover:text-red-600 hover:bg-red-50'
                      : 'text-gray-400 hover:text-red-400 hover:bg-red-400/10'
                  }`}>
                    <Youtube className="w-3 h-3" />
                  </a>
                  <a href="#" className={`p-1 transition-all duration-300 rounded hover:scale-110 ${
                    theme === 'light'
                      ? 'text-gray-500 hover:text-blue-700 hover:bg-blue-50'
                      : 'text-gray-400 hover:text-blue-500 hover:bg-blue-500/10'
                  }`}>
                    <Linkedin className="w-3 h-3" />
                  </a>
                  <a href="https://x.com/neurosparktechnologies" className={`p-1 transition-all duration-300 rounded hover:scale-110 ${
                    theme === 'light'
                      ? 'text-gray-500 hover:text-gray-900 hover:bg-gray-100'
                      : 'text-gray-400 hover:text-white hover:bg-gray-400/10'
                  }`}>
                    <X className="w-3 h-3" />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Main Navigation */}
      <div className={`transition-all duration-500 ${
        isMainPage && !scrolled ? 'lg:opacity-0 lg:-translate-y-full lg:pointer-events-none opacity-100 translate-y-0' : 'opacity-100 translate-y-0'
      }`}>
        <div className="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            
            {/* Logo */}
            <div className="flex items-center space-x-2 cursor-pointer group" onClick={handleLogoClick}>
              <div className="relative">
                <img 
                  src="/logo.png" 
                  alt="NeuroSpark Technologies" 
                  className="object-contain w-10 h-10 transition-all duration-300 filter brightness-110 contrast-110 saturate-150 group-hover:scale-110"
                  style={{
                    filter: 'brightness(1.1) contrast(1.1) saturate(1.5) hue-rotate(10deg)'
                  }}
                  loading="lazy"
                  decoding="async"
                />
              </div>
              <div className="block">
                <span className={`text-lg font-bold lg:text-xl font-display ${
                  theme === 'light' ? 'text-gray-900' : 'text-white'
                }`}>NeuroSpark</span>
                <div className={`text-xs font-medium -mt-0.5 ${
                  theme === 'light' ? 'text-gray-600' : 'text-gray-400'
                }`}>Technologies</div>
              </div>
            </div>

            {/* Desktop Menu */}
            <div className="hidden md:block">
              <div className="flex items-center space-x-1">
                {location.pathname === '/apnaeduhub' ? (
                  <>
                    <button 
                      onClick={() => scrollToSection('home')} 
                      className={`px-3 py-1.5 text-sm font-medium transition-all duration-300 rounded-lg ${
                        theme === 'light'
                          ? 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                          : 'text-gray-300 hover:text-white hover:bg-white/10'
                      }`}
                    >
                      Home
                    </button>
                    <button 
                      onClick={() => scrollToSection('design')} 
                      className={`px-3 py-1.5 text-sm font-medium transition-all duration-300 rounded-lg ${
                        theme === 'light'
                          ? 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                          : 'text-gray-300 hover:text-white hover:bg-white/10'
                      }`}
                    >
                      Design
                    </button>
                    <button 
                      onClick={() => scrollToSection('features')} 
                      className={`px-3 py-1.5 text-sm font-medium transition-all duration-300 rounded-lg ${
                        theme === 'light'
                          ? 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                          : 'text-gray-300 hover:text-white hover:bg-white/10'
                      }`}
                    >
                      Features
                    </button>
                    <button 
                      onClick={() => scrollToSection('contact')} 
                      className={`px-3 py-1.5 text-sm font-medium transition-all duration-300 rounded-lg ${
                        theme === 'light'
                          ? 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                          : 'text-gray-300 hover:text-white hover:bg-white/10'
                      }`}
                    >
                      Contact
                    </button>
                    <button 
                      onClick={() => scrollToSection('faq')} 
                      className={`px-3 py-1.5 text-sm font-medium transition-all duration-300 rounded-lg ${
                        theme === 'light'
                          ? 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                          : 'text-gray-300 hover:text-white hover:bg-white/10'
                      }`}
                    >
                      FAQ
                    </button>
                  </>
                ) : (
                  <>
                    <button 
                      onClick={() => scrollToSection('home')} 
                      className={`px-3 py-1.5 text-sm font-medium transition-all duration-300 rounded-lg ${
                        theme === 'light'
                          ? 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                          : 'text-gray-300 hover:text-white hover:bg-white/10'
                      }`}
                    >
                      Home
                    </button>
                    <button 
                      onClick={() => scrollToSection('about')} 
                      className={`px-3 py-1.5 text-sm font-medium transition-all duration-300 rounded-lg ${
                        theme === 'light'
                          ? 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                          : 'text-gray-300 hover:text-white hover:bg-white/10'
                      }`}
                    >
                      About
                    </button>
                    <button 
                      onClick={() => scrollToSection('founder')} 
                      className={`px-3 py-1.5 text-sm font-medium transition-all duration-300 rounded-lg ${
                        theme === 'light'
                          ? 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                          : 'text-gray-300 hover:text-white hover:bg-white/10'
                      }`}
                    >
                      Founder
                    </button>
                    <button 
                      onClick={() => scrollToSection('products')} 
                      className={`px-3 py-1.5 text-sm font-medium transition-all duration-300 rounded-lg ${
                        theme === 'light'
                          ? 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                          : 'text-gray-300 hover:text-white hover:bg-white/10'
                      }`}
                    >
                      Products
                    </button>
                    <button 
                      onClick={() => scrollToSection('contact')} 
                      className={`px-3 py-1.5 text-sm font-medium transition-all duration-300 rounded-lg ${
                        theme === 'light'
                          ? 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                          : 'text-gray-300 hover:text-white hover:bg-white/10'
                      }`}
                    >
                      Contact
                    </button>
                  </>
                )}
                
                {/* CTA Button */}
                <div className={`pl-3 ml-3 border-l ${
                  theme === 'light' ? 'border-gray-300' : 'border-gray-700'
                }`}>
                  <button 
                    onClick={() => scrollToSection('contact')}
                    className="px-4 py-2 text-sm font-semibold text-white transition-all duration-300 transform rounded-lg shadow-lg bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 hover:scale-105 hover:shadow-blue-500/25"
                  >
                    Get Started
                  </button>
                </div>
              </div>
            </div>

            {/* Mobile Menu Button */}
            <div className="flex items-center gap-3 md:hidden">
              <ThemeToggle className="scale-90" position="relative" />
              <button
                onClick={() => setIsOpen(!isOpen)}
                className={`p-1.5 transition-colors duration-300 rounded-lg focus:outline-none ${
                  theme === 'light'
                    ? 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                    : 'text-gray-300 hover:text-white hover:bg-white/10'
                }`}
              >
                {isOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </button>
            </div>
          </div>

          {/* Mobile Menu */}
          {isOpen && (
            <div className="md:hidden">
              <div className={`px-2 pt-2 pb-3 space-y-1 border-t rounded-b-xl ${
                theme === 'light'
                  ? 'bg-white border-gray-200 shadow-lg'
                  : 'glass border-white/10'
              }`}>
                {location.pathname === '/apnaeduhub' ? (
                  <>
                    <button 
                      onClick={() => scrollToSection('home')} 
                      className={`block w-full px-3 py-2 text-sm font-medium text-left transition-all duration-300 rounded-lg ${
                        theme === 'light'
                          ? 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                          : 'text-gray-300 hover:text-white hover:bg-white/10'
                      }`}
                    >
                      Home
                    </button>
                    <button 
                      onClick={() => scrollToSection('design')} 
                      className={`block w-full px-3 py-2 text-sm font-medium text-left transition-all duration-300 rounded-lg ${
                        theme === 'light'
                          ? 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                          : 'text-gray-300 hover:text-white hover:bg-white/10'
                      }`}
                    >
                      Design
                    </button>
                    <button 
                      onClick={() => scrollToSection('features')} 
                      className={`block w-full px-3 py-2 text-sm font-medium text-left transition-all duration-300 rounded-lg ${
                        theme === 'light'
                          ? 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                          : 'text-gray-300 hover:text-white hover:bg-white/10'
                      }`}
                    >
                      Features
                    </button>
                    <button 
                      onClick={() => scrollToSection('contact')} 
                      className={`block w-full px-3 py-2 text-sm font-medium text-left transition-all duration-300 rounded-lg ${
                        theme === 'light'
                          ? 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                          : 'text-gray-300 hover:text-white hover:bg-white/10'
                      }`}
                    >
                      Contact
                    </button>
                    <button 
                      onClick={() => scrollToSection('faq')} 
                      className={`block w-full px-3 py-2 text-sm font-medium text-left transition-all duration-300 rounded-lg ${
                        theme === 'light'
                          ? 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                          : 'text-gray-300 hover:text-white hover:bg-white/10'
                      }`}
                    >
                      FAQ
                    </button>
                  </>
                ) : (
                  <>
                    <button 
                      onClick={() => scrollToSection('home')} 
                      className={`block w-full px-3 py-2 text-sm font-medium text-left transition-all duration-300 rounded-lg ${
                        theme === 'light'
                          ? 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                          : 'text-gray-300 hover:text-white hover:bg-white/10'
                      }`}
                    >
                      Home
                    </button>
                    <button 
                      onClick={() => scrollToSection('about')} 
                      className={`block w-full px-3 py-2 text-sm font-medium text-left transition-all duration-300 rounded-lg ${
                        theme === 'light'
                          ? 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                          : 'text-gray-300 hover:text-white hover:bg-white/10'
                      }`}
                    >
                      About
                    </button>
                    <button 
                      onClick={() => scrollToSection('founder')} 
                      className={`block w-full px-3 py-2 text-sm font-medium text-left transition-all duration-300 rounded-lg ${
                        theme === 'light'
                          ? 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                          : 'text-gray-300 hover:text-white hover:bg-white/10'
                      }`}
                    >
                      Founder
                    </button>
                    <button 
                      onClick={() => scrollToSection('products')} 
                      className={`block w-full px-3 py-2 text-sm font-medium text-left transition-all duration-300 rounded-lg ${
                        theme === 'light'
                          ? 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                          : 'text-gray-300 hover:text-white hover:bg-white/10'
                      }`}
                    >
                      Products
                    </button>
                    <button 
                      onClick={() => scrollToSection('contact')} 
                      className={`block w-full px-3 py-2 text-sm font-medium text-left transition-all duration-300 rounded-lg ${
                        theme === 'light'
                          ? 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                          : 'text-gray-300 hover:text-white hover:bg-white/10'
                      }`}
                    >
                      Contact
                    </button>
                  </>
                )}
                
                {/* Mobile CTA */}
                <div className={`pt-3 border-t ${
                  theme === 'light' ? 'border-gray-200' : 'border-white/10'
                }`}>
                  <button 
                    onClick={() => scrollToSection('contact')}
                    className="w-full px-4 py-2 font-semibold text-white transition-all duration-300 rounded-lg shadow-lg bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
                  >
                    Get Started
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </nav>
  );
};

export default Navbar;